/**
 * Farkle scoring. This file is the referee, and it is deliberately the only
 * place in the codebase that knows what a die is worth.
 *
 * The rule that shapes everything here: **a keep must be fully scoring.** You
 * may not set aside a 3 alongside your 1 "because it came with it". So the
 * central question is never "what is this roll worth" but "is this exact set of
 * dice legal, and if so, what is the best reading of it" — and those are two
 * different computations, which is why `scoreKeep` and `bestKeep` are separate.
 *
 * Why a search rather than a lookup table: the flat n-of-a-kind values below
 * genuinely conflict with the single-die values, and the conflict is not a bug
 * in the rules — it is in every printed rulebook. Four 1s is 1000 as a
 * four-of-a-kind, but 1100 read as a triplet plus a single. The player is
 * entitled to the better reading, always, and only a search over partitions
 * finds it reliably. Hand-written precedence rules get this wrong on exactly the
 * hands people remember.
 */

export type Face = 1 | 2 | 3 | 4 | 5 | 6;
export const DICE_COUNT = 6;

/** A face-indexed tally. Index 0 is unused so `counts[3]` means "how many 3s". */
export type Counts = [number, number, number, number, number, number, number];

export function tally(dice: readonly number[]): Counts {
  const counts: Counts = [0, 0, 0, 0, 0, 0, 0];
  for (const d of dice) {
    if (!Number.isInteger(d) || d < 1 || d > 6) throw new RangeError(`not a die face: ${d}`);
    counts[d] += 1;
  }
  return counts;
}

/**
 * What a run of the same face is worth on its own.
 *
 * Three 1s are 1000 rather than 100 — the one asymmetry every table agrees on.
 * Four, five and six of a kind are flat regardless of face, which is the most
 * widely played variant and the one the scorekeeper's reference card lists.
 */
function runValue(face: number, n: number): number {
  switch (n) {
    case 3:
      return face === 1 ? 1000 : face * 100;
    case 4:
      return 1000;
    case 5:
      return 2000;
    case 6:
      return 3000;
    default:
      return 0;
  }
}

/** Singles. Only 1s and 5s score alone; everything else is dead on its own. */
function singleValue(face: number): number {
  if (face === 1) return 100;
  if (face === 5) return 50;
  return 0;
}

// ── the six-dice specials ───────────────────────────────────────────────────

/**
 * These only exist as readings of all six dice at once, and each consumes the
 * whole hand. They are checked before the per-face search because none of them
 * decompose into runs and singles — a straight contains no scoring run at all,
 * and read die-by-die it is worth 150.
 */
const STRAIGHT = 1500;
const THREE_PAIRS = 1500;
const TWO_TRIPLETS = 2500;

function sixDiceSpecial(counts: Counts): number {
  let pairs = 0;
  let triplets = 0;
  let singles = 0;
  let quads = 0;
  let sixes = 0;
  for (let f = 1; f <= 6; f++) {
    const c = counts[f]!;
    if (c === 1) singles += 1;
    else if (c === 2) pairs += 1;
    else if (c === 3) triplets += 1;
    else if (c === 4) quads += 1;
    else if (c === 6) sixes += 1;
  }
  let best = 0;
  // 1-2-3-4-5-6. Every face exactly once.
  if (singles === 6) best = Math.max(best, STRAIGHT);
  // Three pairs. A four-of-a-kind plus a pair reads as three pairs at most
  // tables, and refusing it costs the player 1500 for a hand that looks like
  // three pairs on the felt — so it counts.
  if (pairs === 3 || (quads === 1 && pairs === 1) || sixes === 1) best = Math.max(best, THREE_PAIRS);
  // Two triplets. Six of a kind is two triplets too, and is worth more read that
  // way at 3000 — the max below settles it either way.
  if (triplets === 2 || sixes === 1) best = Math.max(best, TWO_TRIPLETS);
  return best;
}

// ── the search ──────────────────────────────────────────────────────────────

const memo = new Map<string, number>();

function key(counts: Counts): string {
  return counts.slice(1).join('');
}

/**
 * The best score that uses EVERY die in `counts`, or -1 if no reading does.
 *
 * Recursion takes the lowest remaining face and tries every legal way to consume
 * it: as a run of 3..n, or — for 1s and 5s only — one at a time as singles. Any
 * face that can be neither is a dead die, and the whole branch fails. Fixing the
 * face at each step is what keeps this from re-deriving the same partition in
 * six different orders.
 */
function bestUsingAll(counts: Counts): number {
  const k = key(counts);
  const hit = memo.get(k);
  if (hit !== undefined) return hit;

  let face = 0;
  for (let f = 1; f <= 6; f++) {
    if (counts[f]! > 0) {
      face = f;
      break;
    }
  }
  // Nothing left to place: this partition was complete and scored everything.
  if (face === 0) {
    memo.set(k, 0);
    return 0;
  }

  const available = counts[face]!;
  let best = -1;

  for (let take = 3; take <= available; take++) {
    counts[face] = available - take;
    const rest = bestUsingAll(counts);
    counts[face] = available;
    if (rest >= 0) best = Math.max(best, runValue(face, take) + rest);
  }

  const single = singleValue(face);
  if (single > 0) {
    counts[face] = available - 1;
    const rest = bestUsingAll(counts);
    counts[face] = available;
    if (rest >= 0) best = Math.max(best, single + rest);
  }

  memo.set(k, best);
  return best;
}

/**
 * Score a set of dice the player wants to set aside.
 *
 * Returns null when the set is not fully scoring — that is the signal the UI
 * uses to grey out the roll button, and the signal the server uses to reject the
 * move. Null and 0 are genuinely different answers: an empty keep scores 0 and
 * is *legal to compute*, it is just not a legal move on its own.
 */
export function scoreKeep(dice: readonly number[]): number | null {
  if (dice.length === 0) return 0;
  if (dice.length > DICE_COUNT) return null;
  const counts = tally(dice);

  let best = -1;
  if (dice.length === DICE_COUNT) best = Math.max(best, sixDiceSpecial(counts));
  best = Math.max(best, bestUsingAll(counts));
  return best < 0 ? null : best;
}

/** Convenience: is this exact set of dice a legal thing to set aside? */
export function isLegalKeep(dice: readonly number[]): boolean {
  return dice.length > 0 && scoreKeep(dice) !== null;
}

// ── reading a roll ──────────────────────────────────────────────────────────

/** One way to play a roll: which dice to set aside, and what it pays. */
export interface Keep {
  /** Indices into the roll, ascending. */
  indices: number[];
  score: number;
  /** Dice left to throw again after taking this. Zero means hot dice. */
  remaining: number;
}

/**
 * Every distinct legal keep available in a roll, best-scoring first.
 *
 * Distinct by the multiset of faces, not by index — with three 5s on the table
 * "keep one 5" is one choice, not three, and showing it three times is how a
 * move list becomes unreadable. The indices returned are the first matching
 * dice, which is all the caller needs to preselect them.
 */
export function allKeeps(roll: readonly number[]): Keep[] {
  const seen = new Map<string, Keep>();
  const n = roll.length;

  for (let mask = 1; mask < 1 << n; mask++) {
    const indices: number[] = [];
    for (let i = 0; i < n; i++) if (mask & (1 << i)) indices.push(i);
    const faces = indices.map((i) => roll[i]!);
    const score = scoreKeep(faces);
    if (score === null || score === 0) continue;
    const sig = [...faces].sort().join('');
    const prev = seen.get(sig);
    if (!prev) seen.set(sig, { indices, score, remaining: n - indices.length });
  }

  return [...seen.values()].sort((a, b) => b.score - a.score || a.indices.length - b.indices.length);
}

/**
 * The highest-scoring legal keep in a roll, or null if the roll is a farkle.
 *
 * Ties break toward *fewer dice*: 500 from two dice leaves four to throw, 500
 * from five leaves one. The scores are equal and the positions are not remotely
 * equal, so the tiebreak is doing real work.
 */
export function bestKeep(roll: readonly number[]): Keep | null {
  const keeps = allKeeps(roll);
  if (keeps.length === 0) return null;
  let best = keeps[0]!;
  for (const k of keeps) {
    if (k.score > best.score || (k.score === best.score && k.indices.length < best.indices.length)) best = k;
  }
  return best;
}

/** The total if you swept up every scoring die in the roll. Also the "max" hint. */
export function maxScore(roll: readonly number[]): number {
  return allKeeps(roll).reduce((best, k) => Math.max(best, k.score), 0);
}

/**
 * A farkle: no die in this roll scores anything, so the turn is over and the
 * points go back to the house. This is the single most consequential question
 * the engine asks, and it is a subset search rather than a shortcut list —
 * `1 2 3 4 5 6` scores through none of the usual signals.
 */
export function isFarkle(roll: readonly number[]): boolean {
  const counts = tally(roll);
  if (roll.length === DICE_COUNT && sixDiceSpecial(counts) > 0) return false;
  for (let f = 1; f <= 6; f++) {
    if (counts[f]! >= 3) return false;
  }
  return counts[1] === 0 && counts[5] === 0;
}

// ── the reference card ──────────────────────────────────────────────────────

/** What the scoring sheet shows. Kept beside the code it describes, on purpose. */
export const RULE_CARD: ReadonlyArray<readonly [string, string]> = [
  ['Single 1', '100'],
  ['Single 5', '50'],
  ['Three 1s', '1,000'],
  ['Three 2s–6s', 'face × 100'],
  ['Four of a kind', '1,000'],
  ['Five of a kind', '2,000'],
  ['Six of a kind', '3,000'],
  ['Straight 1–6', '1,500'],
  ['Three pairs', '1,500'],
  ['Two triplets', '2,500'],
];
